export default {
  init () {
    this.focusIndex = -1
    this.loadingText = 'Loading...'
    this.noResultsText = 'No results found'
    this.responseCodes = {
      success: 200,
      multipleChoices: 300
    }
    this.debounceTimer = null // Timer for debouncing input
    this.pendingRequest = null // Current XMLHttpRequest to allow cancellation
    this.lastSearchString = '' // Track last search to avoid redundant requests

    this.setupSearch()

    if (!this.searchInput || !this.domSuggestions) {
      return
    }

    this.loadingOption()
    this.noResultsOption()
    this.addSearchInputListeners()
  },

  setupSearch () {
    this.searchInput = document.getElementById('search-input') || document.getElementById('results-search-input')
    this.domSuggestions = document.getElementById('suggestions')
  },

  async getSearchSuggestions (searchString) {
    return new Promise((resolve, reject) => {
      this.makeSearchRequest(searchString, (err, success) => {
        if (err) {
          reject(err)
          return
        }

        resolve(success)
      })
    })
  },

  makeSearchRequest (searchString, callback) {
    // Cancel any pending request
    if (this.pendingRequest) {
      this.pendingRequest.abort()
      this.pendingRequest = null
    }

    const xhr = new globalThis.XMLHttpRequest()
    this.pendingRequest = xhr // Store reference for cancellation

    xhr.open('GET', `/suggestions?searchString=${searchString}`, true)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.onload = () => {
      this.pendingRequest = null // Clear reference on completion
      if (xhr.status >= this.responseCodes.success && xhr.status < this.responseCodes.multipleChoices) {
        callback(null, xhr.response ? JSON.parse(xhr.response) : null)
      } else {
        const error = new Error(`Request failed with status ${xhr.status}: ${xhr.statusText}`)
        error.status = xhr.status
        error.statusText = xhr.statusText
        callback(error)
      }
    }
    xhr.onerror = () => {
      this.pendingRequest = null // Clear reference on error
      const error = new Error(`Request failed with status ${xhr.status}: ${xhr.statusText}`)
      error.status = xhr.status
      error.statusText = xhr.statusText
      callback(error)
    }
    xhr.onabort = () => {
      this.pendingRequest = null // Clear reference on abort
      // Don't call callback on abort - request was intentionally cancelled
    }
    xhr.send(JSON.stringify({
      searchString
    }))
  },

  getOption (value, text, onClick, onMouseOver = null, classList = '') {
    const option = document.createElement('div')
    option.value = value
    option.textContent = text
    option.onmousedown = onClick
    option.onmouseover = onMouseOver

    if (classList) {
      classList.split(/\s+/).forEach(cls => option.classList.add(cls))
    }

    return option
  },

  loadingOption () {
    return this.getOption(this.loadingText, this.loadingText,
      () => { this.searchInput.focus() },
      null,
      'mpdp-text-colour-dark-grey')
  },

  noResultsOption () {
    return this.getOption(this.noResultsText, this.noResultsText,
      () => { this.searchInput.focus() },
      null,
      'mpdp-text-colour-dark-grey')
  },

  viewAllOption (count) {
    const text = `View all (${count} ${count === 1 ? 'result' : 'results'})`
    return this.getOption(
      text,
      text,
      () => document.getElementById('search-button')?.click(),
      null,
      'mpdp-text-align-center mpdp-align-items-center mpdp-option-container mpdp-break-word')
  },

  hideSuggestions () {
    this.domSuggestions.style.display = 'none'
    this.focusIndex = -1
    this.unsetActive()
  },

  showSuggestions () {
    this.domSuggestions.style.display = 'block'
  },

  async loadSuggestions () {
    // Loading state is now shown in oninput handler for immediate feedback

    try {
      const searchString = encodeURIComponent(this.searchInput.value)
      const suggestions = await this.getSearchSuggestions(searchString)

      if (suggestions?.count) {
        this.domSuggestions.innerHTML = ''
        suggestions.rows.forEach(row => {
          const val = `${row.payee_name} (${row.town}, ${row.county_council}, ${row.part_postcode})`
          this.domSuggestions.append(this.getOption(val, val,
            () => {
              globalThis.location.href = `${globalThis.location.origin}/details?payeeName=${encodeURIComponent(row.payee_name)}&partPostcode=${row.part_postcode}&searchString=${searchString}`
            },
            (event) => {
              event.stopPropagation()
              if (this.focusIndex !== -1) {
                this.focusIndex = -1
                this.unsetActive()
              }
            },
            'mpdp-option-container mpdp-break-word'
          ))
        })

        this.domSuggestions.append(this.viewAllOption(suggestions.count))
        return
      }
    } catch (e) {
      // Ignore errors from aborted requests
      if (e.message?.includes('abort')) {
        return
      }
      console.error(e)
    }
    this.domSuggestions.innerHTML = this.noResultsOption().outerHTML
  },

  setActive () {
    if (!this.domSuggestions.children?.length) {
      return
    }

    this.unsetActive()

    if (this.focusIndex < 0) {
      this.focusIndex = this.domSuggestions.children.length - 1
    }

    if (this.focusIndex >= this.domSuggestions.children.length) {
      this.focusIndex = 0
    }

    if ([this.loadingText, this.noResultsText].includes(this.domSuggestions.children[this.focusIndex].textContent)) {
      return
    }

    this.domSuggestions.children[this.focusIndex].classList.add('active')
  },

  unsetActive () {
    for (const child of this.domSuggestions.children) {
      child.classList.remove('active')
    }
  },

  addSearchInputListeners () {
    const minCharLength = 3
    const timeout = 250
    const debounceDelay = 500 // Wait 500ms after user stops typing (more aggressive)

    this.searchInput.onblur = () => {
      globalThis.setTimeout(() => {
        this.hideSuggestions()
      }, timeout)
    }

    this.searchInput.oninput = () => {
      // Clear any existing debounce timer
      if (this.debounceTimer) {
        globalThis.clearTimeout(this.debounceTimer)
        this.debounceTimer = null
      }

      if (this.searchInput.value.trim().length < minCharLength) {
        this.hideSuggestions()
        // Cancel any pending request when below min length
        if (this.pendingRequest) {
          this.pendingRequest.abort()
          this.pendingRequest = null
        }
        return
      }

      // Show loading state immediately for better UX
      this.domSuggestions.innerHTML = this.loadingOption().outerHTML
      this.showSuggestions()

      // Debounce: wait for user to stop typing before making request
      this.debounceTimer = globalThis.setTimeout(() => {
        this.debounceTimer = null
        const currentSearch = this.searchInput.value.trim()

        // Skip if same as last search (redundant)
        if (currentSearch === this.lastSearchString) {
          return
        }

        this.lastSearchString = currentSearch
        this.loadSuggestions()
      }, debounceDelay)
    }

    this.searchInput.onkeydown = e => {
      if (e.defaultPrevented) {
        return // do nothing if the event was already processed
      }

      if (['Down', 'ArrowDown'].includes(e.key)) {
        this.focusIndex++
        this.setActive()
      } else if (['Up', 'ArrowUp'].includes(e.key)) {
        this.focusIndex--
        this.setActive()
      } else if (['Esc', 'Escape'].includes(e.key)) {
        this.hideSuggestions()
      } else if (e.key === 'Enter') {
        if (this.focusIndex === -1 || !this.domSuggestions.children.length) {
          document.getElementById('search-button')?.click()
          return
        }

        if ([this.loadingText, this.noResultsText].includes(this.domSuggestions.children[this.focusIndex].value)) {
          return
        }

        this.domSuggestions.children[this.focusIndex].dispatchEvent(new globalThis.MouseEvent('mousedown'))
      } else {
        return
      }

      e.preventDefault()
    }
  }
}
